import BaseService from "../base";

/**
 * Provides services related to step base data, extending BaseService.
 */
export default class StepBaseService extends BaseService {
  // Stores step base data
  stepBaseData = {
    procedureFolder: null,
    supportingFolder: null,
    shareTask: null,
    procedureOwner: null,
    primaryProcedureOwner: null,
    applyJBStatus: null,
    applyPmpExcluded: null,
    tlReview: null,
    signOff: null,
    taskInfomation: null,
  };

  /**
   * Retrieves the extension key.
   * @returns {null} Returns null, overridden by subclasses for specific implementation.
   */
  getExtensionKey = () => {
    return null;
  };

  /**
   * Retrieves the PMP Excluded step key.
   * @returns {string} Returns the PMP Excluded step key.
   */
  getPMPExcludedStepKey = () => {
    return null;
  };

  /**
   * Retrieves the task information key.
   * @returns {string} Returns the task information key.
   */
  getTaskInfoKey = () => {
    return null;
  };

  /**
   * Retrieves the procedure file step key.
   * @returns {string} Returns the procedure file step key.
   */
  getProcedureFileStepKey = () => {
    return null;
  };

  /**
   * Retrieves the supporting file step key.
   * @returns {string} Returns the supporting file step key.
   */
  getSupportingFileStepKey = () => {
    return null;
  };

  /**
   * Retrieves the primary procedure owner step key.
   * @returns {string} Returns the primary procedure owner step key.
   */
  getPrimaryProcedureOwnerStepKey = () => {
    return null;
  };

  /**
   * Retrieves the procedure owner step key.
   * @returns {string} Returns the procedure owner step key.
   */
  getProcedureOwnerStepKey = () => {
    return null;
  };

  /**
   * Retrieves the shared task step key.
   * @returns {string} Returns the shared task step key.
   */
  getShareTaskStepKey = () => {
    return null;
  };

  /**
   * Retrieves the list of step business keys.
   * @returns {Array} Returns an array containing all step business keys.
   */
  getStepBusinessKeyList = () => {
    let list = [
      this.getProcedureFileStepKey(),
      this.getSupportingFileStepKey(),
      this.getPrimaryProcedureOwnerStepKey(),
      this.getProcedureOwnerStepKey(),
      this.getShareTaskStepKey(),
      this.getPMPExcludedStepKey(),
      this.getTaskInfoKey(),
    ];

    list = list.filter((x) => x != null);
    const extensionKey = this.getExtensionKey();
    if (extensionKey !== null) {
      list.push(extensionKey);
    }

    return list;
  };

  /**
   * Retrieves all step business data.
   * @param {object} param Parameters object to be sent.
   * @returns {Promise} Returns a Promise that resolves with all step business data asynchronously.
   */
  getStepBusinessData = (param: any, interfaceList: any, mergePayloadMap: any) => {
    const stepBusinessKeyList = interfaceList ?? this.getStepBusinessKeyList();
    return Promise.all(
      this.getAllRestSend(
        stepBusinessKeyList,
        param,
        interfaceList === undefined || interfaceList === null,
        mergePayloadMap
      )
    );
  };

  /**
   * Sends requests for all steps and collects data.
   * @param {Array} stepBusinessKeyList Array of step business keys.
   * @param {object} param Parameters object.
   * @returns {Array} Returns an array of asynchronous request actions.
   */
  getAllRestSend = (
    stepBusinessKeyList: any,
    param: any,
    loadFlowData: any,
    mergePayloadMap: any
  ) => {
    const allAction = stepBusinessKeyList.map((stepBusinessKey: any) => {
      let mergeParam = param;

      if (mergePayloadMap && mergePayloadMap[stepBusinessKey.settingKey]) {
        const { isOverRide, ...mergePayloadItem } = mergePayloadMap[stepBusinessKey.settingKey];

        if (isOverRide) {
          mergeParam = mergePayloadItem;
        } else {
          //allows for more flexible merging of objects where the casing of keys might not be consistent.
          mergeParam = {
            ...mergeParam,
            ...Object.keys(mergePayloadItem).reduce((acc, key) => {
              const matchKey =
                Object.keys(mergeParam).find((k) => k.toLowerCase() === key.toLowerCase()) || key;
              // @ts-ignore
              acc[matchKey] = mergePayloadItem[key];
              return acc;
            }, {}),
          };
        }
      }

      // @ts-ignore
      return this.sendRequest(
        stepBusinessKey.settingKey,
        mergeParam,
        (response: any) => {
          // @ts-ignore
          this.stepBaseData[stepBusinessKey.dataKey] = response;
        },
        (error: any) => {
          console.log(error);
        }
      );
    });

    if (loadFlowData) {
      allAction.push(this.getFlowStepData(param));
    }

    return allAction;
  };

  /**
   * Retrieves flow step data.
   * @param {object} param Parameters object.
   * @returns {Promise} Returns a Promise that resolves with the flow step data asynchronously.
   */
  getFlowStepData = (param: any) => {
    // @ts-ignore
    return this.sendRequest(
      "getFlowStepData",
      param,
      (response: any) => {
        if (response === null) {
          return;
        }

        for (const key in response) {
          // @ts-ignore
          this.stepBaseData[key] = response[key];
        }
      },
      (error: any) => {
        console.log(error);
      }
    );
  };

  /**
   * Retrieves step data, calling success callback on success and error callback on failure.
   * @param {object} param Parameters object.
   * @param {Function} success Callback function called on success.
   * @param {Function} error Callback function called on failure.
   */
  getStepData = (
    param: any,
    success: any,
    error: any,
    interfaceList: any,
    mergePayloadMap: any
  ) => {
    return this.getStepBusinessData(param, interfaceList, mergePayloadMap)
      .then(() => {
        success(this.stepBaseData);
      })
      .catch((err) => {
        error(err);
      });
  };
}
